import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Database } from 'lucide-react';
import PageLayout from '@components/layout/PageLayout';
import Tabs, { TabItem } from '@components/common/Tabs';
import ErrorBoundary from '@components/common/ErrorBoundary';
import ManualImport from '@components/features/import/ManualImport';
import ImportSettings from '@components/features/import/ImportSettings';
import SystemParametersSettings from '@components/features/system-parameters/SystemParametersSettings';
import ScopeRulesSettings from '@components/features/import/ScopeRulesSettings';
import { ImportJobsTab } from '@components/features/import-jobs';
import { AuditLogExport } from '@components/features/audit';
import styles from './AdminDataManagementPage.module.css';

const tabs: TabItem[] = [
  { key: 'manual-import', label: 'Manual Import' },
  { key: 'import-jobs', label: 'Import Jobs' },
  { key: 'settings', label: 'Settings' },
  { key: 'scope-rules', label: 'Scope Rules' },
  { key: 'audit-log', label: 'Audit Log' },
];

const DEFAULT_TAB = 'manual-import';

const AdminDataManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  // Fall back to the default tab if the URL has no ?tab= or an unrecognized one,
  // so a bare /admin/data-management (and old bookmarks) still work.
  const activeTab = tabs.some((tab) => tab.key === requestedTab) ? (requestedTab as string) : DEFAULT_TAB;

  const setActiveTab = (tabKey: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabKey);
    setSearchParams(next, { replace: true });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'import-jobs':
        return (
          <ErrorBoundary level="page">
            <ImportJobsTab />
          </ErrorBoundary>
        );
      case 'settings':
        return (
          <ErrorBoundary level="page">
            <ImportSettings />
            <SystemParametersSettings />
          </ErrorBoundary>
        );
      case 'scope-rules':
        return (
          <ErrorBoundary level="page">
            <ScopeRulesSettings />
          </ErrorBoundary>
        );
      case 'audit-log':
        return (
          <ErrorBoundary level="page">
            <AuditLogExport />
          </ErrorBoundary>
        );
      case 'manual-import':
      default:
        return (
          <ErrorBoundary level="page">
            <ManualImport />
          </ErrorBoundary>
        );
    }
  };

  return (
    <PageLayout title="Data Management" showHeader={false}>
      <div className={styles.header} data-test-id="data-management-page">
        <h1 className={styles.title}>
          <Database size={24} />
          Data Management
        </h1>
      </div>

      <div className={styles.container}>
        <Tabs items={tabs} activeTab={activeTab} onTabChange={setActiveTab} className={styles.tabs} />

        <div className={styles.content}>{renderTabContent()}</div>
      </div>
    </PageLayout>
  );
};

export default AdminDataManagementPage;
